/**
 * The gateway's contract, mirrored — paths, request and response bodies, and
 * the error envelope.
 *
 * One place, because the client had none: every module invented its own shape
 * against a platform that already publishes one, and some of those shapes are
 * refused. The launch body is the sharpest example. It DECODES STRICTLY
 * (`dec.DisallowUnknownFields()`, scanapi.go), carries exactly
 * `targets` / `include` / `exclude` / `auth_profiles`, and this console was
 * built to send a scan profile, a report template and a schedule alongside
 * them. Not one of those is a field, so the whole request is rejected — not the
 * extra keys, the request. That is a guaranteed failure the moment HTTP mode is
 * switched on, and it is invisible while everything is mocked.
 *
 * Nothing here calls anything. It is the shape the mock layer maps onto and the
 * shape an HTTP layer will serialise, so the two cannot drift apart.
 *
 * Mirrored from `control/internal/scanapi`, `control/internal/targetapi`,
 * `control/cmd/gateway/server.go` and `shared/errors`, read at source.
 */

/* ─────────────── Paths ─────────────── */

/**
 * Exactly what the gateway serves.
 *
 * The scan-run read and transition MOVED to `/v1/scans/{scan_run_id}`, with no
 * project segment — S-172's published address, which S-178 had found could not
 * be built as ruled because removing the segment deletes what the authorization
 * object is constructed from. That was resolved upstream and the routes now
 * live at the published address; the LAUNCH verb stays nested inside its
 * project, which is the split RAD §5.2 always described.
 */
export const paths = {
  provisionOrganisation: () => "/v1/admin/organisations",
  /** `GET` on the provisioning path is the KYC review QUEUE (ADR-066). */
  kycQueue: () => "/v1/admin/organisations",
  organisationStanding: (organisationId: string) =>
    `/v1/admin/organisations/${encodeURIComponent(organisationId)}`,
  organisationKyc: (organisationId: string) =>
    `/v1/admin/organisations/${encodeURIComponent(organisationId)}/kyc`,

  /** The LAUNCH verb — nested, because a run is created inside a project. */
  scans: (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}/scans`,
  /** The READ and TRANSITION verbs — by run id alone. */
  scan: (scanRunId: string) => `/v1/scans/${encodeURIComponent(scanRunId)}`,
  scanFindings: (scanRunId: string) => `${paths.scan(scanRunId)}/findings`,
  scanWaveState: (scanRunId: string) => `${paths.scan(scanRunId)}/wave-state`,
  /**
   * A PROJECT's findings, across every run in it (S-402, served since 2.420).
   *
   * Nested under the project because the project is what is being asked about,
   * which is the same shape as `scans` above. It is NOT the issues list: FR-54a
   * promotes findings to issues by org-configurable rules, so unpromoted
   * findings exist by design and the issues address cannot answer this.
   */
  projectFindings: (projectId: string) =>
    `/v1/projects/${encodeURIComponent(projectId)}/findings`,

  /**
   * FR-09's machine credentials (CMP-010). Org-scoped with no id in the path:
   * every key a caller can reach belongs to the organisation its credential
   * resolved to, and the server confines the lookup with an explicit `org_id`
   * predicate because `api_keys` carries no row-level security (DAT-026).
   */
  /**
   * FR-02's roster and invitation. Org-scoped with no id in the path: the tenant
   * comes off the credential, never the request (invariant 8).
   *
   * `/v1/people` rather than `/v1/members` because half the rows are NOT members —
   * an invited address has accepted nothing — and naming them members in the URL
   * would make the claim FR-02's "no uninvited user can join" exists to deny.
   */
  people: () => "/v1/people",
  /** The caller's own role at sign-in: platform operator, and the organisation that resolves (the coordinator's design, 2026-10-14). */
  sessionCapabilities: () => "/v1/session/capabilities",
  peopleInvitations: () => "/v1/people/invitations",
  peopleInvitation: (id: string) => `/v1/people/invitations/${id}`,
  /** FR-02a (ADR-108): a joined member, by user id, for DELETE (remove, or leave). */
  peopleMember: (userId: string) => `/v1/people/members/${encodeURIComponent(userId)}`,
  /** FR-02b (S-1045): PUT makes a joined member an Org Admin, DELETE makes them a member again. */
  peopleMemberAdmin: (userId: string) => `/v1/people/members/${encodeURIComponent(userId)}/admin`,
  /**
   * S-709, ADR-106 (2.790): a subject's effective permissions — the decision
   * point's evaluation with the grants that explain it. A person's user id, an
   * API key's id, or the caller's own `sub`; the organisation is the token's.
   */
  personPermissions: (userId: string) => `/v1/people/${encodeURIComponent(userId)}/permissions`,

  apiKeys: () => "/v1/api-keys",
  apiKey: (apiKeyId: string) => `/v1/api-keys/${encodeURIComponent(apiKeyId)}`,
  apiKeyRotate: (apiKeyId: string) => `${paths.apiKey(apiKeyId)}/rotate`,

  /** FR-69's rules and FR-68's in-app notifications (ADR-075, `notifyapi`). */
  alertRules: () => "/v1/alert-rules",
  // FR-59, ADR-086: the organisation's risk weights.
  riskWeights: () => "/v1/risk-weights",
  riskWeightsHistory: () => "/v1/risk-weights/history",
  alertRule: (ruleId: string) => `/v1/alert-rules/${encodeURIComponent(ruleId)}`,
  notifications: () => "/v1/notifications",
  /** FR-67's customer webhooks (ADR-073, `webhookapi`) — every verb `admin`. */
  webhooks: () => "/v1/webhooks",
  webhook: (webhookId: string) => `/v1/webhooks/${encodeURIComponent(webhookId)}`,
  /** FR-68's Slack destinations (ADR-077, `slackapi`) — every verb `admin`. */
  slackDestinations: () => "/v1/slack-destinations",
  slackDestination: (destinationId: string) => `/v1/slack-destinations/${encodeURIComponent(destinationId)}`,
  webhookDeliveries: (webhookId: string) => `/v1/webhooks/${encodeURIComponent(webhookId)}/deliveries`,
  webhookDelivery: (webhookId: string, deliveryId: string) =>
    `/v1/webhooks/${encodeURIComponent(webhookId)}/deliveries/${encodeURIComponent(deliveryId)}`,
  notificationRead: (notificationId: string) =>
    `/v1/notifications/${encodeURIComponent(notificationId)}/read`,

  scanProfiles: () => "/v1/scan-profiles",
  projects: () => "/v1/projects",

  finding: (findingId: string) => `/v1/findings/${encodeURIComponent(findingId)}`,
  findingArtefact: (findingId: string, artefactType: string) =>
    `${paths.finding(findingId)}/artefacts/${encodeURIComponent(artefactType)}`,

  organisations: () => "/v1/orgs",
  organisationWorkspaces: (organisationId: string) =>
    `${paths.organisations()}/${encodeURIComponent(organisationId)}/workspaces`,

  /**
   * FR-87's third budget layer. A NARROW resource rather than a PATCH on the
   * organisation, and the narrowness is load-bearing: migration 0060 revoked the
   * tenant role's UPDATE on `plan_tier` and left `strix_token_budget` writable,
   * so a verb carrying the whole organisation would be one field away from
   * letting a tenant set its own plan tier.
   */
  organisationAIBudget: (organisationId: string) =>
    `${paths.organisations()}/${encodeURIComponent(organisationId)}/ai-budget`,

  /**
   * FR-04's write addresses (S-234). The create verbs are NESTED under the
   * parent that scopes them — a workspace is created in an organisation, a
   * project in a workspace — while rename and delete are flat by id, the same
   * split every other resource here uses.
   */
  workspace: (workspaceId: string) => `/v1/workspaces/${encodeURIComponent(workspaceId)}`,
  workspaceProjects: (workspaceId: string) => `${paths.workspace(workspaceId)}/projects`,
  project: (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}`,
  // FR-23, S-684 (ADR-100): a project's schedules, and one by its own id.
  projectSchedules: (projectId: string) => `${paths.project(projectId)}/schedules`,
  // FR-64 (IFC-017, ADR-098): the trend series, per project and per organisation.
  projectTrends: (projectId: string) => `${paths.project(projectId)}/trends`,
  organisationTrends: (organisationId: string) => `/v1/orgs/${encodeURIComponent(organisationId)}/trends`,
  /** 2.976 (vision): the pre-aggregated summaries the overview and dashboard read. */
  organisationSummary: (organisationId: string) => `/v1/orgs/${encodeURIComponent(organisationId)}/summary`,
  projectSummary: (projectId: string) => `${paths.project(projectId)}/summary`,
  /** 2.979 (vision): FR-87's model roster (prices folded in) and the organisation's selected model. */
  aiModels: () => "/v1/ai/models",
  organisationAiProfile: (organisationId: string) => `/v1/orgs/${encodeURIComponent(organisationId)}/ai-profile`,
  schedule: (scheduleId: string) => `/v1/schedules/${encodeURIComponent(scheduleId)}`,
  // FR-87 project level (S-680): PUT only; the value is read off the project.
  projectAiTesting: (projectId: string) => `${paths.project(projectId)}/ai-testing`,

  reports: () => "/v1/reports",
  // S-708 (the sponsor's ruling: list past reports): a project's reports, newest first.
  projectReports: (projectId: string) => `${paths.project(projectId)}/reports`,
  report: (reportId: string) => `/v1/reports/${encodeURIComponent(reportId)}`,
  reportContent: (reportId: string) => `${paths.report(reportId)}/content`,

  issues: () => "/v1/issues",
  // FR-54a, S-612: the organisation's promotion rule and its recorded history.
  promotionRule: () => "/v1/promotion-rule",
  promotionRuleHistory: () => "/v1/promotion-rule/history",
  issue: (issueId: string) => `/v1/issues/${encodeURIComponent(issueId)}`,
  /**
   * IFC-016's list verb addressed at ONE project.
   *
   * A SECOND address for the same rows, not a replacement: `issues()` answers
   * "everything I can see" and this answers "this project". The gateway
   * authorizes on the project in the path, so a caller with no relation on it
   * is REFUSED — where the org-wide route would simply not include it.
   */
  projectIssues: (projectId: string) => `${paths.project(projectId)}/issues`,

  roles: () => "/v1/roles",
  role: (roleId: string) => `/v1/roles/${encodeURIComponent(roleId)}`,
  roleAssignees: (roleId: string) => `${paths.role(roleId)}/assignees`,
  roleGrants: (roleId: string) => `${paths.role(roleId)}/grants`,

  targets: (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}/targets`,
  target: (projectId: string, targetId: string) =>
    `${paths.targets(projectId)}/${encodeURIComponent(targetId)}`,

  authProfiles: (projectId: string, targetId: string) =>
    `${paths.target(projectId, targetId)}/auth-profiles`,
  authProfile: (projectId: string, targetId: string, authProfileId: string) =>
    `${paths.authProfiles(projectId, targetId)}/${encodeURIComponent(authProfileId)}`,
  authProfileRevoke: (projectId: string, targetId: string, authProfileId: string) =>
    `${paths.authProfile(projectId, targetId, authProfileId)}/revoke`,
  // FR-29, CMP-025 (2.792): one auth profile's CSRF settings; GET reads, PUT replaces.
  authProfileSessionGuard: (projectId: string, targetId: string, authProfileId: string) =>
    `${paths.authProfile(projectId, targetId, authProfileId)}/session-guard`,

  // FR-62a, ADR-085: the customer's own compliance scope declarations.
  complianceScope: (projectId: string, targetId: string) =>
    `${paths.target(projectId, targetId)}/compliance-scope`,
  // FR-62a: one framework's declaration history, newest first (2.660).
  complianceScopeHistory: (projectId: string, targetId: string, framework: string) =>
    `${paths.complianceScope(projectId, targetId)}/${encodeURIComponent(framework)}/history`,
  // FR-30, DAT-121 (2.787): a target's confirmed logout links; GET reads, PUT replaces.
  logoutTerms: (projectId: string, targetId: string) => `${paths.target(projectId, targetId)}/logout-terms`,
  // FR-14a, ADR-088: archive a target (irreversible).
  targetArchive: (projectId: string, targetId: string) => `${paths.target(projectId, targetId)}/archive`,
  // FR-16, 2.622: POST uploads a schema (the body IS the document); the
  // current import and its operations are read beside it.
  targetSchemaImports: (projectId: string, targetId: string) =>
    `${paths.target(projectId, targetId)}/schema-imports`,
  targetSchemaImportCurrent: (projectId: string, targetId: string) =>
    `${paths.targetSchemaImports(projectId, targetId)}/current`,
} as const;

/* ─────────────── Scans ─────────────── */

/** DAT-030's role, on the wire. */
export type WireAuthProfileRole = "primary" | "inject" | "observe";

export interface WireLaunchAuthProfile {
  id: string;
  /** Required. DAT-030 gives no default and RAD §8.2.10's "never derived" applies. */
  role: WireAuthProfileRole;
}

/**
 * `POST /v1/projects/{project_id}/scans`.
 *
 * These four fields and NOTHING else — the decoder rejects unknown fields, so
 * an extra key fails the whole request rather than being ignored. There is
 * deliberately no attestation field: `scan_runs.authorization_attestation`
 * records why a run was ALLOWED, and it used to be filled with whatever the
 * caller sent, which made the subject of the check its author.
 */
export interface WireLaunchRequest {
  /**
   * 2.849 (S-834; 2.825, S-165, ADR-115, PROVISIONAL): the registered target
   * the scan is launched against. The console's ONLY way to name what a scan
   * scans: the run's seed is that target's saved address, which the platform
   * reads and re-checks itself.
   *
   * There is deliberately NO `targets` field on this type any more. It is the
   * OPERATOR-ONLY address list (`openapi.yaml` LaunchRequest), refused 403
   * `FORBIDDEN` without platform scope — and the gateway treats a PRESENT
   * `targets`, even `[]`, as that list (`addressList := body.Targets != nil`,
   * control/internal/scanapi/scanapi.go:444), so the key must be absent, not
   * empty.
   */
  target_id: string;
  include?: string[];
  exclude?: string[];
  auth_profiles?: WireLaunchAuthProfile[];
  /**
   * REQUIRED — no `omitempty` on the Go side.
   *
   * S-166 closed and the selection now reaches the Dispatcher, so the ONE thing
   * invariant 14 permits a user to choose is finally carried. Until it landed
   * this client dropped the profile deliberately, because nothing stored it;
   * sending it then failed the whole request under strict decoding, and NOT
   * sending it fails now. The direction reversed, which is why the mirror is
   * re-read from source rather than remembered.
   */
  scan_profile: string;
}

export interface WireLaunchResponse {
  scan_run_id: string;
  state: string;
}

/* ─────────────── Targets ─────────────── */

export interface WireCreateTargetRequest {
  url: string;
  criticality: string;
  exposure: string;
  tags?: string[];
}

export interface WireTarget {
  id: string;
  project_id: string;
  url: string;
  criticality: string;
  exposure: string;
  tags?: string[];
  /** FR-14a, ADR-088: set once the target is archived. */
  archived_at?: string;
  archived_by?: string;
  /**
   * 2.734 (S-760): the live auth profiles (neither revoked nor being revoked).
   * Required by the contract, typed `unknown` because an older platform omits
   * it; the Targets list reads it strictly (2.793).
   */
  live_auth_profile_count?: unknown;
  /**
   * 2.849 (S-165, ADR-115): why a launch will refuse this target — SDD
   * Appendix A *Target unscannable reason*. Present only on a target saved
   * before create refused such an address. Typed `unknown` and read strictly
   * (`unscannableReasonFromWire`): a non-string is not a reason.
   */
  unscannable_reason?: unknown;
}

/**
 * SDD Appendix A *Target unscannable reason* (ADR-115, PROVISIONAL), verbatim
 * and in its order — `openapi.yaml` Target.unscannable_reason's enum.
 */
export const WIRE_UNSCANNABLE_REASONS = [
  "userinfo_not_allowed", "target_not_single_host", "target_never_scan",
] as const;

export type WireUnscannableReason = (typeof WIRE_UNSCANNABLE_REASONS)[number];

/* ─────────────── Schema import (FR-16, IFC-013) ─────────────── */

/**
 * FR-16 — an API schema import of a target (2.622, DAT-100, DAT-101).
 *
 * SOURCE-MIRRORED from `control/internal/targetapi/schemaimport.go`:
 * `importResponse` (schemaimport.go:63) and `operationResponse`
 * (schemaimport.go:79), whose `params` are `schemaimport.Param`
 * (`control/internal/schemaimport/model.go:71`). It replaces a spec-derived
 * shape that carried a `graphql` format, a stored `failed` status and a
 * per-endpoint `source` tag, none of which the platform has.
 *
 * What the platform keeps is the NAMES AND SHAPE of the declared operations —
 * method, path, parameter locations and names, body kind, operationId — never a
 * value, a server URL or the document itself. A refused document is NOT
 * stored: the refusal is the upload's 400, and a read never sees it.
 */
export type WireSchemaFormat = "openapi" | "postman";

/** SDD Appendix A "Schema parameter location" — never a header or a cookie. */
export type WireSchemaParamLocation = "query" | "path" | "body";

/** SDD Appendix A "Schema body kind". */
export type WireSchemaBodyKind = "none" | "json" | "form" | "multipart" | "xml";

export interface WireSchemaParam {
  in: WireSchemaParamLocation;
  name: string;
}

export interface WireSchemaOperation {
  method: string;
  /** Relative to the target's origin; a whole-segment `{name}` is a path parameter. */
  path: string;
  /** The operation on the TARGET's origin. Absent when the target URL cannot be read as one. */
  url?: string;
  params: WireSchemaParam[];
  body: WireSchemaBodyKind;
  operation_id?: string;
}

export interface WireSchemaImport {
  target_id: string;
  seq: number;
  format: WireSchemaFormat;
  /** The document's own version (`3.0.3`, `2.0`, `2.1`); empty when not a short dotted number. */
  spec_version: string;
  sha256: string;
  bytes: number;
  base_path: string;
  operation_count: number;
  /** Warning code → how many times. Codes are `SCHEMA_IMPORT_WARNINGS`; `{}` when none. */
  warnings: Record<string, number>;
  imported_by: string;
  imported_at: string;
  /** Served by the current-import read only; the upload's 201 omits it. */
  operations?: WireSchemaOperation[];
}

/**
 * A refusal's `details.issue` — `schemaimport.Issue` (model.go:179): a code
 * and integers, never document text. Each integer is omitted when unknown.
 */
export interface WireSchemaIssue {
  code: string;
  line?: number;
  column?: number;
  count?: number;
}

/** SDD Appendix A "Schema import warning" — what an accepted import dropped or changed, counted. */
export const SCHEMA_IMPORT_WARNINGS = [
  "ref_not_local", "ref_unresolved", "param_location_not_carried", "param_name_refused", "path_refused",
  "method_refused", "params_exceeded", "literal_withheld", "identifier_templated", "body_not_carried",
  "operation_id_dropped", "duplicate_operation", "servers_ignored", "request_skipped",
] as const;

export type WireSchemaWarning = (typeof SCHEMA_IMPORT_WARNINGS)[number];

/** SDD Appendix A "Schema import refusal" — `details.issue.code` of a 400. */
export const SCHEMA_IMPORT_REFUSALS = [
  "document_too_large", "syntax", "duplicate_key", "yaml_alias_refused", "depth_exceeded", "nodes_exceeded",
  "dereferences_exceeded", "operations_exceeded", "deadline_exceeded", "not_openapi", "not_postman",
  "unsupported_version", "base_path_refused", "no_operations", "media_type_refused",
] as const;

export type WireSchemaRefusal = (typeof SCHEMA_IMPORT_REFUSALS)[number];

/* ─────────────── Auth profiles ─────────────── */

export interface WireCreateAuthProfileRequest {
  type: string;
  config?: Record<string, unknown>;
  /** FR-26a's tenant-supplied CA bundle. Public material, so it appears in reads. */
  trust_anchor_pem?: string;
  /**
   * The only field on this surface carrying material. A map because a
   * credential's FIELD NAMES are the customer's — fixing them here would fix
   * which logins the platform can perform.
   */
  credential: Record<string, string>;
}

/**
 * What a read returns. No credential and no vault reference, and neither is an
 * omission a client may work around: the credential is the customer's property
 * (CON-007), and the vault location is an internal address the platform is free
 * to change.
 */
export interface WireAuthProfile {
  id: string;
  target_id: string;
  type: string;
  config?: Record<string, unknown>;
  trust_anchor_pem?: string;
  /** Present ONLY on a revoked profile — absent and "it works" are the same statement. */
  revoked_at?: string;
}

/* ─────────────── Envelopes ─────────────── */

export interface WireCreated {
  id: string;
}

/**
 * One page and the cursor for the next.
 *
 * CURSOR, not page-and-size. The console's mock layer invented
 * `{items, total, page, pageSize}`, which the platform does not offer and
 * cannot cheaply compute — a total requires counting rows RLS has to filter.
 * `next_cursor` is empty when the page was not full, which is the honest
 * signal: a full page MIGHT have more and a short one cannot.
 */
/**
 * A custom role as the platform records it (IFC-012, FR-11).
 *
 * There is no `permissions` field and there is no `built_in`. A role's authority
 * lives in the authorization graph as GRANTS, read separately through
 * `roleGrants` — the two are different stores and the platform never conflates
 * them. The console must not either: a role row carries a name, not a power.
 */
export interface WireRole {
  role_id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

/**
 * One capability a role holds on one project (`RoleGrant` in the contract).
 *
 * `capability` is a CLOSED enum server-side. It is typed as `string` here on
 * purpose: a capability the platform adds before this console knows the name is
 * still a real grant, and the page shows it by id rather than dropping it. See
 * `lib/rbac/capabilities.ts` — under-reporting what a role holds is the one
 * direction a permissions screen must never be wrong in.
 */
export interface WireRoleGrant {
  project_id: string;
  capability: string;
}

/**
 * One holder of a role — `GET /v1/roles/{role_id}/assignees` (S-468).
 *
 * `user_id` AND NOTHING ELSE, because a holder is not necessarily a person. The
 * platform renders every authorization subject as `user:<id>`, an organisation
 * API key included (FR-09), and a key joins the graph on its first request
 * exactly as a colleague does — so a key can hold a role and appears in no people
 * roster. The server therefore cannot name these, and does not try; the console
 * resolves an id against the people roster, then against the API keys, and shows
 * one it can resolve against neither AS the id. Dropping it would hide authority
 * the administrator cannot then remove.
 */
export interface WireRoleAssignee {
  user_id: string;
}

export interface WirePage<T> {
  items: T[];
  next_cursor?: string;
}

/* ─────────────── Errors ─────────────── */

/**
 * `shared/errors`. Stable machine codes (SDD Appendix B) — a client branches on
 * `code`, never on `message`, which is prose and may be reworded.
 */
export const WIRE_ERROR_CODES = [
  "KYC_NOT_VERIFIED", "SCOPE_FORBIDDEN", "SCOPE_TOO_BROAD", "ENTITLEMENT_EXCEEDED",
  "CAPACITY_QUEUED", "INVALID_TRANSITION", "VALIDATION_REQUIRED", "AUTH_PROFILE_FAILED",
  "ENGINE_UNAVAILABLE", "BUDGET_EXCEEDED", "IDEMPOTENCY_CONFLICT", "DEPENDENCY_DEGRADED",
  "AI_BUDGET_NONE", "AI_BUDGET_NOT_CONFIGURED", "AI_TOKEN_CEILING_EXHAUSTED",
  "INVALID_REQUEST", "UNAUTHORIZED", "FORBIDDEN", "NOT_FOUND",
  "METHOD_NOT_ALLOWED", "CONFLICT", "RATE_LIMITED", "QUEUE_FULL", "ARTEFACT_TOO_LARGE", "INTERNAL",
  "CHANGE_OUTCOME_UNKNOWN",
] as const;

export type WireErrorCode = (typeof WIRE_ERROR_CODES)[number];

export interface WireError {
  code: WireErrorCode;
  message: string;
  /** Structured, caller-safe context — a field name, a limit, a state. */
  details?: Record<string, unknown>;
  /** Correlates the response with the operator-side record. */
  trace_id?: string;
  retryable: boolean;
}

export function isWireError(value: unknown): value is WireError {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<WireError>;
  return (
    typeof candidate.code === "string" &&
    (WIRE_ERROR_CODES as readonly string[]).includes(candidate.code) &&
    typeof candidate.message === "string" &&
    typeof candidate.retryable === "boolean"
  );
}

/* ─────────────── Tenancy ─────────────── */

export interface WireOrganisation {
  organisation_id: string;
  name: string;
  kyc_status: string;
  plan_tier: string;
  authorised_signatory?: string;
  strix_token_budget: number;
}

/**
 * `PUT /v1/orgs/{organisation_id}/ai-budget`, request and response alike.
 *
 * `strix_token_budget` is REQUIRED and is not the same as omitted: zero is a
 * real budget meaning the organisation may spend nothing, while a missing field
 * is a malformed request, "because reading it as zero would set a spending
 * limit the caller never asked for" (openapi.yaml). So this is not optional
 * here either — a `?` would let a caller construct the malformed body.
 */
export interface WireAITokenBudget {
  strix_token_budget: number;
}

/**
 * A project belongs to a WORKSPACE, not to an organisation.
 *
 * This is the tier RAD §8.1 requires and the console does not model — its
 * `Project.orgId` says a project hangs off an organisation, and the platform
 * says otherwise. Recorded here rather than smoothed over: the mapping is
 * currently possible only because S-177 gives a login exactly one
 * organisation, so "the org this project is in" is never ambiguous. The moment
 * a person can see two, this breaks and the tier has to be modelled properly.
 */
export interface WireProject {
  project_id: string;
  workspace_id: string;
  name: string;
  /** FR-87 project level (S-680): false unless a project admin enabled it. */
  ai_testing_enabled?: boolean;
}

/** `PUT /v1/projects/{project_id}/ai-testing`'s answer (S-680). */
export interface WireProjectAITesting {
  project_id: string;
  enabled: boolean;
}

export interface WireWorkspace {
  workspace_id: string;
  organisation_id: string;
  name: string;
}

/* ─────────────── Findings (IFC-015) ─────────────── */

/**
 * `app.severity`. Note `info`, not `informational` — the console's own
 * `Severity` spells it the long way, and the two are mapped rather than
 * assumed equal.
 */
export type WireSeverity = "info" | "low" | "medium" | "high" | "critical";

/**
 * `app.confidence`. An ENUM, not a percentage.
 *
 * The console rendered `confidence` as `{n}%`. There is no number behind that
 * on this platform — FR-49 scores confidence in bands, where `confirmed`
 * means proof was obtained rather than "100% likely". Showing a percentage
 * would invent a precision the pipeline never claims.
 */
export type WireConfidence = "low" | "medium" | "high" | "confirmed";

/**
 * `app.validation_result`.
 *
 * **`discarded` IS NOT RETURNED, and this comment used to say the opposite
 * (S-295).** `api/openapi.yaml` publishes the enum as `[verified, unverified]`
 * and says why in terms: *"`discarded` never appears: a contradicted finding is
 * discarded rather than stored at lower confidence (ARC-036, invariant 3)."*
 *
 * The old reading cited invariant 3 correctly and inverted it. Invariant 3 says
 * such a finding is never STORED; the comment read that as "stored and returned,
 * so the console must filter it out". MEASURED 2026-09-08 against every finding
 * the platform has ever stored: 1,015 rows, `verified` and `unverified` only,
 * **zero** discarded — because there is nothing to store, not because a query
 * hides them.
 *
 * The member is KEPT in the union deliberately. It is not a claim that the value
 * arrives; it is what makes `isReportable` type-check as a guard against a
 * contract that could one day widen, which QA-004 (*"never surfaces to a user or
 * report"*) would still require.
 */
export type WireValidationResult = "verified" | "unverified" | "discarded";

/**
 * SDD Appendix A's `verification disposition`, verbatim and in its order.
 *
 * WHY A VALUE AND NOT A UNION ALONE, on `WIRE_SCAN_STATES`' reasoning: a cause
 * this console does not recognise must be reported as unrecognised rather than
 * mapped to a neighbour. Collapsing `never_dispatched` (the PLATFORM never ran
 * the check) into `not_applicable` (nothing could have) is the same class of
 * error FR-24a forbids for run states, and it is the one that matters most
 * here — one is our shortfall and the other is a property of the finding.
 *
 * Eighteen values since `fingerprint_held` (S-1162: a report stored at once,
 * without a verification of its own, because the same finding was already held
 * for another engine's confirm-only check), after seventeen since
 * `reprobe_silent` (2.784; the detection lane's 2.763: a second engine's
 * re-probe ran to completion and reported nothing), after sixteen since
 * `verdict_lost` (S-655, ADR-099). `reproduced` is the
 * only one a `verified` finding carries;
 * `unrecorded` is what findings stored before the marker existed carry, and
 * SDD 8.3 says the platform "says so rather than guessing". `contradicted`
 * has no disposition because such a finding is discarded, never stored
 * (invariant 3).
 */
export const WIRE_VERIFICATION_DISPOSITIONS = [
  "reproduced", "not_applicable", "no_reproduction", "unsupported",
  "run_terminal", "never_dispatched", "never_started", "fingerprint_held", "timed_out",
  "reprobe_silent", "verdict_lost",
  "auth_failed", "attempt_failed", "no_observations", "inconclusive",
  "not_contradictable", "target_distress", "unrecorded",
] as const;

export type WireVerificationDisposition = (typeof WIRE_VERIFICATION_DISPOSITIONS)[number];

export interface WireFinding {
  finding_id: string;
  /** The platform's normalised class. */
  class: string;
  /** What the engine called it. Untrusted text (SARD P2) — escape, never interpolate. */
  native_class: string;
  cwe?: string;
  owasp?: string;
  severity: WireSeverity;
  confidence: WireConfidence;
  validation_result: WireValidationResult;
  /**
   * WHY the verdict is what it is (SDD 8.3's durable marker, S-158). REQUIRED
   * by `api/openapi.yaml` on every finding. Typed `string` rather than the
   * union so a value this console does not know is carried rather than coerced.
   */
  verification_disposition: string;
  target_url: string;
  /** ADR-025's cross-engine agreement. Raises standing; never lowers it. */
  corroboration_count: number;
  /**
   * Whether the platform supplied at least one value somewhere in THIS SCAN
   * that this finding may rest on. Scan-scoped, not per request. REQUIRED and
   * always present: `null` means not recorded, never `observed`. Typed
   * `unknown` because the boundary checks it (`requestProvenanceFromWire`).
   */
  request_provenance: unknown;
  /** Invariant 4: findings are immutable, so a correction supersedes. */
  supersedes?: string;
  created_at: string;
}

/**
 * An evidence REFERENCE, not evidence. RAD §5.2 calls the detail "finding
 * detail with evidence"; DAT-024 requires the objects themselves to be
 * brokered, so what travels here is what a client needs to decide whether to
 * fetch one.
 */
export interface WireArtefactRef {
  type: string;
  content_type: string;
  sha256: string;
  bytes: number;
  truncated: boolean;
  original_bytes?: number;
}

export interface WireFindingDetail extends WireFinding {
  scan_run_id: string;
  artefacts: WireArtefactRef[];
}

/* ─────────────── Scan runs (IFC-014) ─────────────── */

/**
 * SDD Appendix A's `scan run state`, verbatim and in its order.
 *
 * Exported as a VALUE because a state off the wire has to be checked against
 * it. A run whose state this console did not recognise must be reported as
 * unrecognised rather than mapped to a neighbour: `completed_partial` collapsed
 * into `completed` is the exact failure FR-24a's acceptance condition forbids.
 */
export const WIRE_SCAN_STATES = [
  "scheduled", "queued", "running", "paused",
  "aborted", "completed", "failed", "completed_partial",
] as const;

export type WireScanState = (typeof WIRE_SCAN_STATES)[number];

/** One run on `GET /v1/projects/{project_id}/scans` (FR-24a). */
export interface WireRunSummary {
  scan_run_id: string;
  state: string;
  /** FR-19's selection. Absent for a run that predates profile selection. */
  scan_profile?: string;
  target_count: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  /**
   * ADR-074, on the list since S-683: the engines an operator restricted the
   * run to, and the platform's statement that its profile's coverage was NOT
   * delivered. Present together, exactly on such a run, worded as the detail.
   */
  engines?: string[];
  engine_filter?: string;
  /**
   * 2.849 (S-165, ADR-115, PROVISIONAL): where the run's seed scope came from
   * — `WIRE_SCOPE_SOURCES`. Required by the contract; typed `unknown` because
   * an older platform omits it, and read strictly (`scopeSourceFromWire`).
   */
  scope_source?: unknown;
  /** The registered target, present exactly on a `target` run (omitempty). */
  target_id?: unknown;
  /** That target's address. */
  target_url?: unknown;
  /** Whether that target is archived NOW; `omitempty`, so absent is false. */
  target_archived?: unknown;
}

/**
 * SDD Appendix A *Scan scope source* (ADR-115, PROVISIONAL), verbatim — the
 * enum `openapi.yaml` publishes on ScanRun and ScanRunSummary. `not_recorded`
 * is the RENDERING of a run created before the column, never stored.
 */
export const WIRE_SCOPE_SOURCES = ["target", "operator_addresses", "retest", "not_recorded"] as const;

export type WireScopeSource = (typeof WIRE_SCOPE_SOURCES)[number];

/** A run's registered target on the status verb (`ScanRun.target`). */
export interface WireRunTarget {
  id: string;
  url: string;
  archived: boolean;
}

/** What a run REACHED, as `(reached, scoped, unknown)` — DAT-032, ADR-027. */
export interface WireReach {
  reached: number;
  scoped: number;
  /**
   * No measurement was recorded for a run that ran. A THIRD value, not a zero:
   * the gateway's own source says "a report that renders it as 0% would
   * reintroduce the defect at the presentation layer". Read this before the ratio.
   */
  unknown: boolean;
}

/** DAT-031: a submitted target the customer's own exclusion removed. */
export interface WireScopeSubtraction {
  entry: string;
  matched_by: string;
}

export interface WireHealthPause {
  paused_at: string;
  recovered_at?: string;
  recovered_host?: string;
}

/** Appendix A's actor kinds, as `statusResponse.StateActorType` emits them. */
// S-689: `api_key` is an organisation's FR-09 key; `service` is the platform's own automation.
export const WIRE_ACTOR_TYPES = ["user", "api_key", "service", "validation_engine"] as const;

/** `GET /v1/scans/{scan_run_id}` (IFC-014's status verb). */
export interface WireScanStatus {
  scan_run_id: string;
  state: string;
  /** 2.849 (ADR-115): see `WireRunSummary.scope_source`. `unknown`: read strictly. */
  scope_source?: unknown;
  /**
   * The registered target the run scanned, present exactly when
   * `scope_source` is `target` — `WireRunTarget` once checked. `unknown`
   * because a malformed one must be reported as not reported, never guessed.
   */
  target?: unknown;
  scan_profile?: string;
  created_at: string;
  updated_at: string;
  targets: string[];
  include?: string[];
  exclude?: string[];
  /** Never null once written (DAT-031). */
  subtracted_scope: WireScopeSubtraction[];
  /** Absent for a run that never dispatched. Absent ≠ zero. */
  reach?: WireReach;
  /**
   * The LATEST transition (S-169): WHY the run is in its state, in the
   * platform's own sentence — FR-25's breach for a target-health pause, what
   * the run skipped or lost for a conclusion (ARC-009). Never client-supplied
   * text, so it is rendered as text and never as markup. Absent on a run
   * recorded before the field existed.
   */
  state_reason?: string;
  /** WHO performed it, by KIND — Appendix A's closed vocabulary. */
  state_actor_type?: string;
  /**
   * WHO, by id, for a person or an API key only (S-689): resolved through the
   * people and API-key lists (S-690). Absent for the platform's own components,
   * whose names are rendered to no tenant.
   */
  state_actor_id?: string;
  /** WHEN — distinct from `updated_at`, which other writes also move. */
  state_changed_at?: string;
  /**
   * The target-health pause (FR-25, ADR-047). PRESENT exactly when the run is
   * paused by the health monitor; ABSENT on a paused run means the pause was
   * not the target's — never "unknown". `recovered_*` present means the run
   * proxy has evidenced a recovery against the host that broke and a resume
   * is owed.
   */
  health?: WireHealthPause;
  /**
   * When a user asked this run to stop (FR-24, DAT-044).
   *
   * Present on ANY state, not only `aborted`. Six paths conclude a run and
   * only one writes `aborted`, so a run that ended `completed`, `failed` or
   * `completed_partial` with this set is one somebody asked to stop and which
   * finished another way FIRST. Both records are true and neither overrides
   * the other — this field is not the state and must never be rendered as it.
   * Absent means nobody asked. WHO asked is deliberately not published; the
   * gateway explains that under `omitted.abort_requested_by`.
   */
  abort_requested_at?: string;
  /**
   * Each field this platform cannot yet answer, and why — keyed by field name.
   *
   * The gateway sends it so that "not measured" and "measured as nothing" do
   * not share an encoding. A console that ignored it would show a blank where
   * the platform supplied an explanation.
   */
  omitted: Record<string, string>;
}

/* ─────────────── Scan Profiles (IFC-013) ─────────────── */

/**
 * A capability the profile invokes, by CAP id.
 *
 * The narrowing is SCS §7's own parenthetical and is omitted when there is
 * none — absent rather than "", so a client cannot mistake "no narrowing" for
 * "a narrowing with no text".
 */
export interface WireCapability {
  id: string;
  narrowing?: string;
}

/**
 * `GET /v1/scan-profiles`.
 *
 * NOT a constant a client may hard-code, and this console did. Which profiles a
 * deployment publishes depends on its engine roster: a profile is offered only
 * when every capability it invokes can be delivered. The gateway's own source
 * says what shipping the full catalogue would cost — "A console that shipped
 * SCS §7's twelve would offer six this deployment refuses, and the user would
 * meet that refusal at launch, after choosing a target, an auth profile and a
 * scope."
 *
 * `intent` and the per-capability narrowings are published for the reason the
 * server states: "Quick Scan" is a name, and a user choosing it is entitled to
 * know it tests CAP-001 at critical/high only.
 */
export interface WireScanProfile {
  id: string;
  name: string;
  intent: string;
  capabilities: WireCapability[];
}

/**
 * Not paginated, and deliberately: the catalogue is twelve rows fixed by
 * SCS §7, of which a deployment publishes a subset. There is no `next_cursor`.
 */
export interface WireScanProfileList {
  items: WireScanProfile[];
}

/* ─────────────── Aborting a run (IFC-014, FR-24) ─────────────── */

/**
 * `PATCH /v1/scans/{scan_run_id}`.
 *
 * ONE transition is served. Pause and resume are specified (FR-24, §7.1) and
 * not built — resume's guard needs a target-health re-validation the platform
 * has no producer for — and the endpoint says so rather than answering
 * `INVALID_TRANSITION`, which would claim the edge is forbidden when it is not.
 */
export interface WireAbortRequest {
  state: "aborted";
}

/**
 * The 202 body. `state` is what the run WAS when the abort was accepted, NOT
 * `aborted`.
 *
 * The distinction is the whole response. The worker tears its workloads down
 * first (§7.1's guard is "workloads terminated first"), so a client that
 * rendered `aborted` here would be reporting an act the platform has not
 * performed — and if the teardown then failed, it would have told someone a
 * scan stopped that is still sending traffic at a customer's system.
 */
export interface WireAbortResponse {
  scan_run_id: string;
  state: string;
  /** Always true on a 202 — the fact the platform actually recorded. */
  abort_requested: boolean;
}

/* ─────────────── Issues (IFC-016) ─────────────── */

/**
 * SDD Appendix A's `issue state`, verbatim.
 *
 * The console had FIVE of its own — `open`, `triaged`, `in_progress`,
 * `resolved`, `ignored` — of which two exist. `resolved` was the dangerous
 * one: it stood for both `verified`, which only the Validation Engine can
 * reach (ARC-039, invariant 5), and `closed`, which a human does. A single
 * word for those two makes "the platform proved this is fixed" and "somebody
 * decided this is fixed" indistinguishable on screen.
 */
export const WIRE_ISSUE_STATES = [
  "open", "triaged", "in_fix", "validating", "verified", "closed", "reopened",
] as const;

export type WireIssueState = (typeof WIRE_ISSUE_STATES)[number];

/**
 * One issue on `GET /v1/issues`.
 *
 * Note what is NOT here: severity, title, category, target URL, CWE. The
 * console's old model carried all five and the platform stores none of them on
 * an issue — an issue is a GROUPING of findings, and the findings carry those
 * facts. `finding_count` and `finding_ids` are the way through.
 */
export interface WireIssue {
  issue_id: string;
  project_id: string;
  /** What the findings were grouped BY. The platform's dedup key. */
  grouping_key: string;
  state: string;
  /**
   * Optimistic concurrency. REQUIRED on a transition, and the platform is
   * explicit about why it is a pointer on its side: an issue is created at
   * version 0, so a client that omitted the field would silently send the
   * guard's zero value and succeed on exactly the newly promoted issues an
   * analyst triages first.
   */
  version: number;
  finding_count: number;
  created_at?: string;
  updated_at?: string;
  /** The latest risk score and its inputs (FR-59, FR-60). Absent until scored. */
  priority?: WireIssuePriority;
  /** Why there is no score (ADR-078). Absent once scored. */
  priority_unscored_reason?: string;
  /**
   * 3.132: WHAT THE ISSUE IS ABOUT (S-1060's server half, issueapi routes.go on
   * demo-vision): fields, not a composed title, so the console decides how a
   * row reads. Omitted by an older server and by the transition verb.
   */
  class?: string;
  native_class?: string;
  target_url?: string;
  target_host?: string;
}

/** `IssuePriority` — one score and every input it reproduces from. */
export interface WireIssuePriority {
  score: number;
  cvss: {
    version: string;
    vector: string;
    base_score: number;
    /** `class_default` is ASSUMED for the class, not assessed for the instance. */
    source: string;
    defaults_version?: string;
  };
  target_id: string;
  asset_criticality: string;
  exposure: string;
  criticality_weight: number;
  exposure_weight: number;
  computed_at: string;
  /** ADR-086: the organisation weight set used (0 = §8.7's defaults), and why. */
  weight_set_seq: number;
  cause: "promotion" | "weights_changed";
}

/** A validation rule attached to a finding in this issue (FR-50, DAT-021). */
export interface WireValidationRule {
  rule_id: string;
  finding_id: string;
  rule_type: string;
  /** ENGINE-DERIVED. Untrusted text (SARD P2) — escape, never interpolate. */
  source: string;
  target_url: string;
  authenticated: boolean;
}

export interface WireIssueDetail extends WireIssue {
  finding_ids: string[];
  validation_rules: WireValidationRule[];
}

/**
 * `PATCH /v1/issues/{issue_id}`.
 *
 * Strict decode on the server, and `version` is required. The endpoint admits
 * only the three targets of an analyst edge — `triaged`, `in_fix`, `closed`.
 * `verified` is absent from its vocabulary deliberately, so the API never
 * advertises it as something a caller may ask for.
 */
export interface WireIssueTransition {
  state: "triaged" | "in_fix" | "closed";
  version: number;
}

/* ─────────────── Reports (IFC-017) ─────────────── */

/** SDD Appendix A's `report type` vocabulary. */
export const WIRE_REPORT_TYPES = [
  "executive_summary", "technical_detail", "remediation", "delta",
] as const;
export type WireReportType = (typeof WIRE_REPORT_TYPES)[number];

/** SDD Appendix A's `report format` vocabulary. */
export const WIRE_REPORT_FORMATS = ["json", "csv", "pdf"] as const;
export type WireReportFormat = (typeof WIRE_REPORT_FORMATS)[number];

/**
 * SDD Appendix A's `report status`.
 *
 * FOUR values, and the platform's own note says why: a client must be able to
 * tell *not started* from *being built* from *failed*. A boolean would make a
 * failed job indistinguishable from one still running.
 */
export const WIRE_REPORT_STATUSES = ["queued", "generating", "completed", "failed"] as const;
export type WireReportStatus = (typeof WIRE_REPORT_STATUSES)[number];

/** `POST /v1/reports`. The project is named in the BODY — §5.2's address. */
export interface WireReportRequest {
  project_id: string;
  report_type: WireReportType;
  format: WireReportFormat;
  /** The run the report covers. */
  scan_run_id?: string;
  /** RAD §8.2.17's vocabulary; accepted only where a content set is loaded (ADR-079). */
  framework?: string;
}

/**
 * The job record, returned by both the request (202) and the poll (200).
 *
 * It carries the artefact's SIZE and HASH but NOT its key: the key is an
 * object-store address, and a client reads the bytes through the brokered
 * `/content` route rather than by knowing where they live (ADR-050, DAT-024).
 */
export interface WireReportJob {
  report_id: string;
  project_id: string;
  report_type: string;
  format: string;
  status: string;
  scan_run_id?: string;
  size_bytes?: number;
  sha256?: string;
  /** Present only on a failed job. */
  failure_code?: string;
  created_at: string;
  updated_at: string;
}

/* ─────────────── Platform admin (IFC-019) ─────────────── */

/**
 * `POST /v1/admin/organisations` — FR-01. Strict decode: these two fields and
 * nothing else. Mirrored from `control/internal/adminapi/adminapi.go`.
 */
export interface WireProvisionRequest {
  name: string;
  admin_user_id: string;
}

/** 201. The three ids the saga created — org, its default workspace and project. */
export interface WireProvisionResponse {
  organisation_id: string;
  workspace_id: string;
  project_id: string;
}

/**
 * SDD Appendix A's `KYC status`, verbatim — and `control/internal/kyc/kyc.go`
 * agrees. FR-03b's lifecycle: Pending → In Review → Verified; Rejected from In
 * Review; Suspended / Expired from Verified; re-verification returns
 * Suspended/Expired to Verified.
 */
export const WIRE_KYC_STATUSES = [
  "pending", "in_review", "verified", "rejected", "suspended", "expired",
] as const;
export type WireKYCStatus = (typeof WIRE_KYC_STATUSES)[number];

/**
 * `POST /v1/admin/organisations/{organisation_id}/kyc` — FR-03a.
 *
 * `reason` and `kyc_reference` are both REQUIRED, and the gateway says why it
 * refuses rather than storing without them: "every change records actor,
 * timestamp, reason, and kyc_reference", the record is immutable once written,
 * and the reference "is the only thread from this record back to the evidence".
 * The actor and timestamp are the platform's; only these two are the caller's.
 *
 * Which statuses are legal FROM the organisation's current one is decided by
 * §7.3's transition table on the server, not here — an illegal move answers
 * INVALID_TRANSITION. Mirrored from `control/internal/adminapi/kyc.go`.
 */
export interface WireKYCRequest {
  status: WireKYCStatus;
  reason: string;
  kyc_reference: string;
  /**
   * REQUIRED when `status` is `verified`, absent otherwise (FR-03a, RAD 0.20).
   *
   * The named legal representative who attested that this organisation
   * authorised the platform to scan its systems. Optional in the TYPE because
   * the obligation is conditional on the destination status; the gateway
   * refuses a verification without it with INVALID_REQUEST.
   */
  authorised_signatory?: string;
  /**
   * OPTIONAL, verification only (FR-03b, ADR-069 clause 1): an earlier expiry,
   * RFC 3339 with a zone. Absent means the configured validity period.
   */
  kyc_expires_at?: string;
}

export interface WireKYCResponse {
  organisation_id: string;
  status: string;
}

/**
 * `KYCStanding` in the contract — one item of `GET /v1/admin/organisations` and
 * the body of `GET /v1/admin/organisations/{organisation_id}` (ADR-066).
 * Mirrored from `control/internal/adminapi/kycreview.go`: the three nullable
 * columns are OMITTED when unset, never sent empty.
 */
export interface WireKYCStanding {
  organisation_id: string;
  name: string;
  kyc_status: string;
  kyc_reference?: string;
  kyc_expires_at?: string;
  kyc_verified_at?: string;
  /**
   * 3.116: FR-05's plan tier, ASKED OF the product lane for the platform read
   * (the sponsor, 2026-10-08: show the plan for the demo, build the change
   * after). Optional until it is served: absent is "not shown", never a tier.
   */
  plan_tier?: string;
}

/** FR-05's tiers (RAD §5: `plan_tier` ENUM). */
export const WIRE_PLAN_TIERS = ["free", "pro", "enterprise", "custom"] as const;
export type WirePlanTier = (typeof WIRE_PLAN_TIERS)[number];

/* ─────────────── SPEC-DERIVED shapes — no Go source yet ─────────────── */

/**
 * Everything below this line is derived from the SPECIFICATION, not mirrored
 * from a handler, because the handler does not exist yet. These surfaces are
 * built ahead of the backend so the console is ready when it lands.
 *
 * That is a different kind of claim from the rest of this file, and it is kept
 * honest two ways:
 *
 *   1. Nothing here has a PATH. `paths` above carries only addresses the
 *      gateway serves; an invented address would be a contract the client wrote
 *      for the server. Each of these modules answers `notImplemented` in http
 *      mode until the route exists.
 *   2. `test/spec-derived-guard.test.ts` FAILS the moment Go source for one of
 *      these concepts appears in `control/`, naming the entry. A spec-derived
 *      shape is a placeholder for a source-mirrored one, and the failure is the
 *      reminder to replace it — drift between the two cannot hide behind a
 *      green gate.
 *
 * `SPEC_DERIVED` is that registry. Each entry names its spec authority and the
 * Go signal that means "the real thing exists now".
 */
/** A shape built ahead of its backend: its spec authority, and the Go signal that says it now exists. */
export interface SpecDerived { concept: string; authority: string; goSignal: string }

export const SPEC_DERIVED: readonly SpecDerived[] = [
  // `schedules` (FR-23, IFC-014) left this registry at 2.717: its Go source is
  // S-684's scheduleapi (slices 1 and 2), and WireSchedule was checked field by
  // field against scheduleResponse at 064c7d93, whose shapes slice 2 kept.
  // `alerts` (FR-67, FR-69, IFC-018, IFC-027) left this registry at 2.554: its
  // Go source landed (`notifyapi`, ADR-075; `webhookapi`, ADR-073) and the
  // guard's failure was the reminder to re-mirror — which 2.554 did.
  // `schema-import` (FR-16, IFC-013) left it the same way when 2.622 served
  // the routes; its shape is now mirrored under "Schema import" above.
];

/**
 * FR-02 — one row of the organisation's roster.
 *
 * SOURCE-MIRRORED from `control/internal/peopleapi.personResponse`. The server joins
 * two authorities: the identity provider on who is a MEMBER, and the platform's own
 * record on who was ASKED.
 *
 * `user_id` is ABSENT for an invited person, because none exists until they accept,
 * and that absence is load-bearing — assigning an FR-11 role needs a user id and the
 * role verb requires the person to already hold membership.
 *
 * There is no `accepted_at` field anywhere: the platform cannot observe acceptance,
 * so `state` is derived server-side at read time rather than stored and left to rot.
 */
export interface WirePerson {
  /** Present only on an `invited` row — the handle DELETE /v1/people/invitations/{id} takes. */
  invitation_id?: string;
  user_id?: string;
  email: string;
  name?: string;
  state: "joined" | "invited";
  invited_at?: string;
  /** FR-02a (ADR-108): present, and true, on a joined row whose removal stands and has not finished. */
  removal_incomplete?: boolean;
}

/**
 * FR-09 — an org-scoped `hev_…` key. DAT-026's row is
 * `(id, org_id, prefix, argon2id_hash, expires_at, revoked_at, created_at)`;
 * what a client may READ is that row minus the hash. The key itself is shown
 * exactly once, in the mint response, and never stored in plaintext anywhere.
 *
 * SOURCE-MIRRORED from `control/internal/apikeyapi.keyResponse` since FR-09
 * landed. It was spec-derived, and it carried a `name` the platform has nowhere
 * to keep: not in DAT-026's enumerated columns, and not in RAD §8.2.18 either
 * (which lists `scope`, `last_used_at` and `created_by`, none of them built —
 * RAD §8 is the LOGICAL model and the SDD is "authoritative wherever the two
 * differ"). The server decodes a mint request strictly, so a client still
 * sending `name` is TOLD rather than watching the value vanish.
 */
export interface WireApiKey {
  id: string;
  organisation_id: string;
  /** The non-secret lookup prefix. What a person sees in a list. */
  prefix: string;
  expires_at: string;
  revoked_at?: string;
  created_at: string;
}

/** The ONE response that carries the key. It is never returned again. */
export interface WireApiKeyMinted extends WireApiKey {
  key: string;
}

/**
 * FR-23 — cron and one-shot scheduling. First derived from the product lane's
 * APPROVED S-684 design ("Published wire shape", delegated ruling 2026-10-01);
 * SOURCE-MIRRORED since 2.717, when S-684's scheduleapi served the routes and
 * these shapes were checked against its scheduleResponse (the note in
 * SPEC_DERIVED above records the concept leaving that registry).
 *
 *   POST | GET /v1/projects/{project_id}/schedules   (GET cursor-paged)
 *   GET | DELETE /v1/schedules/{schedule_id}
 *   PATCH /v1/schedules/{schedule_id}  {enabled}     — enabling makes the CALLER the owner
 *
 * No `window` until S-684's slice 3 (the server refuses one), and never
 * `engines` or `discovery` (invariant 14: users choose scan profiles).
 */
export interface WireScheduleCreate {
  target_id: string;
  scan_profile: string;
  auth_profiles: WireLaunchAuthProfile[];
  include?: string[];
  exclude?: string[];
  /** IANA name, REQUIRED; "UTC" is valid. Governs the cron (R1). */
  timezone: string;
  /** EXACTLY ONE of these. */
  cron?: string;
  run_at?: string;
}

/** SDD Appendix A's fire outcome, as the design names it. A value outside it is shown verbatim. */
export type WireScheduleFireOutcome =
  | "launched" | "queued" | "refused" | "held" | "skipped_overlap" | "missed" | "authz_unavailable";

export interface WireScheduleLastFire {
  at: string;
  scheduled_for: string;
  /** Typed as string: a value the console does not know is rendered as given, never dropped. */
  outcome: WireScheduleFireOutcome | (string & {});
  code?: string;
  scan_run_id?: string;
  /** S-684 slice 1: for `missed`, how many instants passed the grace, and the first. */
  missed_count?: number;
  missed_first?: string;
}

export interface WireSchedule extends WireScheduleCreate {
  schedule_id: string;
  project_id: string;
  enabled: boolean;
  /** Re-checked at every fire (R6). Transferred to the caller by PATCH {enabled: true}. */
  owner: { type: "user" | "api_key" | (string & {}); id: string };
  /** The platform's own computation, and the authority on when it fires next. */
  next_run_at?: string;
  last_fire?: WireScheduleLastFire;
  /** Still enabled; heals on its own. The token is the platform's and NOT settled — passed through. */
  held_reason?: string;
  /** Disabled until re-enabled. The token is the platform's and NOT settled — passed through. */
  disabled_reason?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/**
 * FR-69's alert rule — SOURCE-MIRRORED from `notifyapi.ruleResponse`
 * (ADR-075). It used to be spec-derived, with no `events` and with `webhook`
 * as a channel; both were wrong: a rule PICKS its events (the sponsor,
 * 2026-09-28), and a customer webhook is a SUBSCRIPTION (ADR-073), never a
 * rule's channel.
 */
export interface WireAlertRule {
  rule_id: string;
  /** `tenant` rules notify every member; `user` rules their owner only. */
  scope: "tenant" | "user";
  /** SDD Appendix A "Alert rule event". */
  events: ("scan.finished" | "finding.stored" | "issue.state_changed")[];
  /**
   * The least severity that fires it. A finding's own; an issue's WORST
   * finding's; a scan event carries none and passes (ADR-075).
   */
  min_severity: WireSeverity;
  /** SDD Appendix A "Alert rule channel". */
  channels: ("in_app" | "email" | "slack")[];
  enabled: boolean;
  created_at: string;
  /** Present exactly when `channels` names `slack` (ADR-077 clause 1). */
  slack_destination_id?: string;
}

/** `POST /v1/alert-rules` — `notifyapi.ruleRequest`, decoded STRICTLY. */
export interface WireAlertRuleCreate {
  scope: "tenant" | "user";
  events: WireAlertRule["events"];
  min_severity: WireSeverity;
  /** The channels a NEW rule may name — the create schema's enum (ADR-075, ADR-076, ADR-077). */
  channels: ("in_app" | "email" | "slack")[];
  /** Required exactly when `channels` names `slack`, refused otherwise. Never a URL. */
  slack_destination_id?: string;
}

/**
 * One Slack destination — `slackapi.destinationResponse` (ADR-077). NEVER its
 * URL: possession of it is the right to post into the channel.
 */
export interface WireSlackDestination {
  id: string;
  name: string;
  /** False once Slack answered 403, 404 or 410; a new destination replaces it. */
  usable: boolean;
  unusable_status?: 403 | 404 | 410;
  created_at: string;
}

/** `POST /v1/slack-destinations` — the URL travels here once and is never returned. */
export interface WireSlackDestinationCreate {
  name: string;
  url: string;
}

/** One in-app notification — `notifyapi.notificationResponse`. */
export interface WireNotification {
  notification_id: string;
  /** Null exactly when `source` is `platform` (ADR-101): no rule raised it. */
  rule_id: string | null;
  /** `rule`: an FR-69 alert rule raised it; `platform`: the platform did, for an `audience`. */
  source: "rule" | "platform" | (string & {});
  /** Present exactly on a platform notification: who may read it. */
  audience?: "org_admins" | (string & {});
  event: WireAlertRule["events"][number] | "schedule.disabled";
  /** Absent for a scan event, which carries none. */
  severity?: WireSeverity;
  /** The PUBLIC event's `data`, as the webhook event of the same name documents it. */
  data: Record<string, unknown>;
  /** Raised by a `tenant` rule: every member sees it, and reads it for themselves. */
  tenant_wide: boolean;
  read: boolean;
  created_at: string;
}

/** `GET /v1/notifications` — `notifyapi.notificationPage`. */
export interface WireNotificationPage {
  items: WireNotification[];
  /** The caller's unread count: the badge (FR-68). */
  unread: number;
  next_cursor?: string;
}

/** SDD Appendix A "Webhook event type" — what a webhook may subscribe to (ADR-073). */
export type WireWebhookEventType = "scan.finished" | "finding.stored" | "issue.state_changed" | "kyc.status_changed";

/**
 * A customer webhook — SOURCE-MIRRORED from `webhookapi.webhookResponse`
 * (FR-67, ADR-073). The FULL url, because every webhook route is `admin`
 * (clause 7). Never the secret. There is no `enabled`: a webhook is live
 * until it is deleted.
 */
export interface WireWebhook {
  id: string;
  organisation_id: string;
  url: string;
  event_types: WireWebhookEventType[];
  created_at: string;
}

/** `POST /v1/webhooks` — `webhookapi.createRequest`, decoded STRICTLY. */
export interface WireWebhookCreate {
  url: string;
  event_types: WireWebhookEventType[];
}

/**
 * The create response. The FIRST carries `secret` — the only time it is ever
 * shown; an idempotent REPLAY carries `secret_already_shown: true` and no
 * secret (S-534).
 */
export type WireWebhookCreated = WireWebhook & ({ secret: string } | { secret_already_shown: true });

/** SDD Appendix A "Webhook delivery status". */
export type WireWebhookDeliveryState = "pending" | "in_flight" | "delivered" | "dead_lettered" | "cancelled" | "dropped";

/** One delivery of one event — `webhookapi.deliveryResponse` (DAT-057). */
export interface WireWebhookDelivery {
  id: string;
  event_id: string;
  event_type: WireWebhookEventType;
  state: WireWebhookDeliveryState;
  /** Attempts charged to the subscriber: six in all before `dead_lettered` (ADR-073 clause 9). */
  subscriber_attempts: number;
  /** Failures the PLATFORM caused, charged to its own budget, never the subscriber's six. */
  platform_failures: number;
  /** SDD Appendix A "Webhook attempt outcome", once an attempt was answered. */
  last_outcome?: string;
  last_http_status?: number;
  /** SDD Appendix A "Webhook dead-letter reason", on a dead letter only. */
  dead_letter_reason?: string;
  next_attempt_at?: string;
  created_at: string;
  finished_at?: string;
}

/** `GET /v1/webhooks/{id}/deliveries` — `webhookapi.deliveryPage`, newest first, pages of 50. */
export interface WireWebhookDeliveryPage {
  items: WireWebhookDelivery[];
  next_cursor?: string;
}

/** One attempt — `webhookapi.attemptResponse` (DAT-058). */
export interface WireWebhookAttempt {
  id: string;
  started_at: string;
  /** The WORKER's resolution: `result`, `no_result` or `publish_failed`. */
  resolution?: string;
  /** The gateway's outcome — present ONLY when the gateway answered. */
  outcome?: string;
  http_status?: number;
  finished_at?: string;
}

/** `GET /v1/webhooks/{id}/deliveries/{delivery_id}` — the exact payload sent, and every attempt. */
export interface WireWebhookDeliveryDetail extends WireWebhookDelivery {
  payload: unknown;
  attempts: WireWebhookAttempt[];
}
